import { useEffect, useRef } from "react";

const scrolls = (overflow: string) => /auto|scroll/.test(overflow);

const canScroll = (element: Element, dx: number, dy: number) => {
  const style = getComputedStyle(element);
  const [delta, overflow, position, size, max] =
    Math.abs(dy) >= Math.abs(dx)
      ? [dy, style.overflowY, element.scrollTop, element.clientHeight, element.scrollHeight]
      : [dx, style.overflowX, element.scrollLeft, element.clientWidth, element.scrollWidth];
  if (!scrolls(overflow)) {
    return false;
  }
  return delta > 0 ? position > 0 : position + size < max - 1;
};

const scrollParent = (element: Element) => {
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (node.scrollHeight > node.clientHeight && scrolls(getComputedStyle(node).overflowY)) {
      return node;
    }
  }
  return null;
};

const textInputTypes = new Set(["text", "search", "email", "url", "tel", "password"]);

const isTextField = (target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement =>
  target instanceof HTMLTextAreaElement || (target instanceof HTMLInputElement && textInputTypes.has(target.type));

// The keyboard shrinks only the visual viewport, so expose its height for the UI to avoid.
// iOS also ignores overflow: hidden on the page, so block touch pans that no inner scroller will consume.
export const useKeyboardInset = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    const element = ref.current;
    if (!viewport || !element) {
      return;
    }

    const isZoomed = () => Math.abs(viewport.scale - 1) > 0.01;
    // User pans are blocked below, so any page scroll is iOS revealing a focused input.
    const resetScroll = () => {
      if (!isZoomed() && (window.scrollY !== 0 || viewport.offsetTop !== 0)) {
        window.scrollTo(0, 0);
      }
    };
    const sync = () => {
      if (isZoomed()) {
        return;
      }
      const inset = Math.max(0, document.documentElement.clientHeight - viewport.height);
      element.style.setProperty("--keyboard-inset", `${inset}px`);
      // scrollIntoView measures against the layout viewport, which the keyboard doesn't shrink.
      const focused = document.activeElement;
      if (isTextField(focused) && element.contains(focused)) {
        const overlap = focused.getBoundingClientRect().bottom + 16 - viewport.offsetTop - viewport.height;
        if (overlap > 0) {
          scrollParent(focused)?.scrollBy(0, overlap);
        }
      }
      resetScroll();
    };

    let lastX = 0;
    let lastY = 0;
    const onTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (touch) {
        lastX = touch.clientX;
        lastY = touch.clientY;
      }
    };
    const onTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch || event.touches.length > 1 || !event.cancelable) {
        return;
      }
      const { clientX, clientY } = touch;
      const dx = clientX - lastX;
      const dy = clientY - lastY;
      lastX = clientX;
      lastY = clientY;
      for (let node = event.target as Element | null; node && node !== document.body; node = node.parentElement) {
        if (canScroll(node, dx, dy)) {
          return;
        }
      }
      event.preventDefault();
    };
    // Native tap-to-focus scrolls the input into view before we learn the keyboard height.
    const onTouchEnd = (event: TouchEvent) => {
      const target = event.target;
      if (isTextField(target) && !target.disabled && document.activeElement !== target && event.cancelable) {
        event.preventDefault();
        target.focus({ preventScroll: true });
      }
    };

    element.classList.add("viewport-lock");
    sync();
    const controller = new AbortController();
    const { signal } = controller;
    viewport.addEventListener("resize", sync, { signal });
    viewport.addEventListener("scroll", resetScroll, { signal });
    document.addEventListener("touchstart", onTouchStart, { passive: true, signal });
    document.addEventListener("touchmove", onTouchMove, { passive: false, signal });
    document.addEventListener("touchend", onTouchEnd, { passive: false, signal });
    return () => {
      controller.abort();
      element.classList.remove("viewport-lock");
      element.style.removeProperty("--keyboard-inset");
    };
  }, []);

  return ref;
};
